import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuthFetch } from '../hooks/useAuthFetch';
import '../stili/AllenamentiForm.css';

const API_URL = process.env.REACT_APP_API_URL;

const TIPI_ALLENAMENTO = ['TECNICO', 'TATTICO', 'ATLETICO'];

const createRow = (defaultCategoriaId = '') => ({
  key: `${Date.now()}-${Math.random()}`,
  idCategoria: defaultCategoriaId,
  dataAllenamento: '',
  oraInizio: '',
  oraFine: '',
  campo: '',
  tipoAllenamento: '',
  faseAttivazione: '',
  durataAttivazione: '',
  faseCentrale: '',
  durataCentrale: '',
  faseFinale: '',
  durataFinale: ''
});

const normalizeCategorie = (data) => {
  if (!Array.isArray(data)) return [];

  return data
    .map((c) => ({
      id: c.id ?? c.idCategoria ?? '',
      nome: c.nome ?? c.nomeCategoria ?? ''
    }))
    .filter((c) => c.id !== '' && c.nome);
};

export default function AllenamentiForm() {
  const authFetch = useAuthFetch();
  const navigate = useNavigate();

  const [allenamenti, setAllenamenti] = useState([createRow()]);
  const [categorie, setCategorie] = useState([]);
  const [loadingCategorie, setLoadingCategorie] = useState(true);
  const [saving, setSaving] = useState(false);

  const categoriaUnicaId = useMemo(() => {
    return categorie.length === 1 ? String(categorie[0].id) : '';
  }, [categorie]);

  const parseErrorMessage = async (res, fallbackMessage) => {
    try {
      const contentType = res.headers.get('content-type') || '';

      if (contentType.includes('application/json')) {
        const data = await res.json();
        return data?.message || data?.error || data?.details || fallbackMessage;
      }

      const text = await res.text();
      return text || fallbackMessage;
    } catch {
      return fallbackMessage;
    }
  };

  useEffect(() => {
    let active = true;

    const fetchCategorie = async () => {
      if (active) setLoadingCategorie(true);

      try {
        const res = await authFetch(`${API_URL}/api/allenamenti/mie-categorie`);

        if (!res.ok) {
          throw new Error(await parseErrorMessage(res, 'Errore nel caricamento categorie'));
        }

        const data = await res.json();

        if (active) {
          setCategorie(normalizeCategorie(data));
        }
      } catch (err) {
        if (active) {
          setCategorie([]);
          toast.error(err.message || 'Errore nel caricamento categorie');
        }
      } finally {
        if (active) setLoadingCategorie(false);
      }
    };

    fetchCategorie();

    return () => {
      active = false;
    };
  }, [authFetch]);

  useEffect(() => {
    if (!categoriaUnicaId) return;

    setAllenamenti((prev) =>
      prev.map((row) => ({
        ...row,
        idCategoria: row.idCategoria || categoriaUnicaId
      }))
    );
  }, [categoriaUnicaId]);

  const handleChange = (index, e) => {
    const { name, value } = e.target;

    setAllenamenti((prev) =>
      prev.map((row, i) =>
        i === index
          ? {
              ...row,
              [name]: value
            }
          : row
      )
    );
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
      e.preventDefault();
    }
  };

  const addRow = () => {
    setAllenamenti((prev) => [...prev, createRow(categoriaUnicaId)]);
  };

  const removeRow = (index) => {
    setAllenamenti((prev) => {
      const updated = prev.filter((_, i) => i !== index);
      return updated.length > 0 ? updated : [createRow(categoriaUnicaId)];
    });
  };

  const resetForm = () => {
    setAllenamenti([createRow(categoriaUnicaId)]);
  };

  const validateDurata = (value, label, riga) => {
    if (value === '' || value === null || value === undefined) return;

    const n = Number(value);

    if (Number.isNaN(n) || n <= 0) {
      throw new Error(`Riga ${riga}: ${label} deve essere maggiore di 0.`);
    }
  };

  const validateRow = (row, rowIndex) => {
    const riga = rowIndex + 1;

    if (!row.idCategoria) {
      throw new Error(`Riga ${riga}: categoria obbligatoria.`);
    }

    if (!row.dataAllenamento) {
      throw new Error(`Riga ${riga}: data allenamento obbligatoria.`);
    }

    if (!row.oraInizio) {
      throw new Error(`Riga ${riga}: ora inizio obbligatoria.`);
    }

    if (!row.oraFine) {
      throw new Error(`Riga ${riga}: ora fine obbligatoria.`);
    }

    if (row.oraFine <= row.oraInizio) {
      throw new Error(`Riga ${riga}: l'ora fine deve essere successiva all'ora inizio.`);
    }

    validateDurata(row.durataAttivazione, 'durata attivazione', riga);
    validateDurata(row.durataCentrale, 'durata fase centrale', riga);
    validateDurata(row.durataFinale, 'durata fase finale', riga);
  };

  const buildPayload = (row) => ({
    idCategoria: Number(row.idCategoria),
    dataAllenamento: row.dataAllenamento,
    oraInizio: row.oraInizio,
    oraFine: row.oraFine,
    campo: row.campo?.trim() || null,
    tipoAllenamento: row.tipoAllenamento || null,
    faseAttivazione: row.faseAttivazione?.trim() || null,
    durataAttivazione: row.durataAttivazione ? Number(row.durataAttivazione) : null,
    faseCentrale: row.faseCentrale?.trim() || null,
    durataCentrale: row.durataCentrale ? Number(row.durataCentrale) : null,
    faseFinale: row.faseFinale?.trim() || null,
    durataFinale: row.durataFinale ? Number(row.durataFinale) : null
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    toast.dismiss();

    try {
      setSaving(true);

      for (let i = 0; i < allenamenti.length; i++) {
        const row = allenamenti[i];
        validateRow(row, i);

        const payload = buildPayload(row);

        const res = await authFetch(`${API_URL}/api/allenamenti`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });

        if (!res.ok) {
          const msg = await parseErrorMessage(
            res,
            `Errore nel salvataggio della riga ${i + 1}`
          );
          throw new Error(msg);
        }
      }

      toast.success('Allenamenti salvati correttamente');
      resetForm();
    } catch (err) {
      toast.error(err.message || 'Errore durante il salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="allenamento-form-container">
      <h2>Inserimento Allenamenti</h2>

      {categoriaUnicaId && categorie[0] && (
        <div className="allenamento-info-box">
          Categoria impostata automaticamente: <strong>{categorie[0].nome}</strong>
        </div>
      )}

      {!categoriaUnicaId && (
        <div className="allenamento-info-box">
          Compila i dati dell’allenamento e le tre fasi operative. Puoi inserire più righe prima del salvataggio.
        </div>
      )}

      <form onSubmit={handleSubmit} onKeyDown={handleKeyDown}>
        <div className="allenamento-table-wrapper">
          <table className="allenamento-table">
            <thead>
              <tr>
                <th>Categoria</th>
                <th>Data</th>
                <th>Ora Inizio</th>
                <th>Ora Fine</th>
                <th>Campo</th>
                <th>Tipo</th>
                <th></th>
              </tr>
            </thead>

            <tbody>
              {allenamenti.map((row, i) => (
                <React.Fragment key={row.key}>
                  <tr>
                    <td>
                      {categoriaUnicaId ? (
                        <input
                          type="text"
                          value={categorie[0]?.nome || ''}
                          readOnly
                          disabled
                        />
                      ) : (
                        <select
                          name="idCategoria"
                          value={row.idCategoria}
                          onChange={(e) => handleChange(i, e)}
                          required
                          disabled={saving || loadingCategorie}
                        >
                          <option value="">
                            {loadingCategorie ? 'Caricamento...' : '-'}
                          </option>
                          {categorie.map((c) => (
                            <option key={c.id} value={c.id}>
                              {c.nome}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>

                    <td>
                      <input
                        type="date"
                        name="dataAllenamento"
                        value={row.dataAllenamento}
                        onChange={(e) => handleChange(i, e)}
                        required
                        disabled={saving}
                      />
                    </td>

                    <td>
                      <input
                        type="time"
                        name="oraInizio"
                        value={row.oraInizio}
                        onChange={(e) => handleChange(i, e)}
                        required
                        disabled={saving}
                      />
                    </td>

                    <td>
                      <input
                        type="time"
                        name="oraFine"
                        value={row.oraFine}
                        onChange={(e) => handleChange(i, e)}
                        required
                        disabled={saving}
                      />
                    </td>

                    <td>
                      <input
                        type="text"
                        name="campo"
                        value={row.campo}
                        onChange={(e) => handleChange(i, e)}
                        placeholder="es. Campo A"
                        maxLength={50}
                        disabled={saving}
                      />
                    </td>

                    <td>
                      <select
                        name="tipoAllenamento"
                        value={row.tipoAllenamento}
                        onChange={(e) => handleChange(i, e)}
                        disabled={saving}
                      >
                        <option value="">-</option>
                        {TIPI_ALLENAMENTO.map((tipo) => (
                          <option key={tipo} value={tipo}>
                            {tipo}
                          </option>
                        ))}
                      </select>
                    </td>

                    <td>
                      <button
                        type="button"
                        onClick={() => removeRow(i)}
                        title="Rimuovi riga"
                        disabled={saving}
                        className="allenamento-remove-btn"
                      >
                        🗑
                      </button>
                    </td>
                  </tr>

                  <tr className="allenamento-descrizione-row">
                    <td colSpan="7">
                      <div className="allenamento-fasi-grid">
                        <div className="allenamento-fase-box">
                          <label htmlFor={`durataAttivazione-${row.key}`}>
                            1 Fase di attivazione
                          </label>

                          <input
                            id={`durataAttivazione-${row.key}`}
                            type="number"
                            name="durataAttivazione"
                            value={row.durataAttivazione}
                            onChange={(e) => handleChange(i, e)}
                            placeholder="Durata in minuti"
                            min="1"
                            disabled={saving}
                          />

                          <textarea
                            id={`faseAttivazione-${row.key}`}
                            name="faseAttivazione"
                            value={row.faseAttivazione}
                            onChange={(e) => handleChange(i, e)}
                            placeholder="Descrivi la fase di attivazione..."
                            rows={4}
                            disabled={saving}
                          />
                        </div>

                        <div className="allenamento-fase-box">
                          <label htmlFor={`durataCentrale-${row.key}`}>
                            2 Fase centrale
                          </label>

                          <input
                            id={`durataCentrale-${row.key}`}
                            type="number"
                            name="durataCentrale"
                            value={row.durataCentrale}
                            onChange={(e) => handleChange(i, e)}
                            placeholder="Durata in minuti"
                            min="1"
                            disabled={saving}
                          />

                          <textarea
                            id={`faseCentrale-${row.key}`}
                            name="faseCentrale"
                            value={row.faseCentrale}
                            onChange={(e) => handleChange(i, e)}
                            placeholder="Descrivi la fase centrale..."
                            rows={5}
                            disabled={saving}
                          />
                        </div>

                        <div className="allenamento-fase-box">
                          <label htmlFor={`durataFinale-${row.key}`}>
                            3 Fase finale
                          </label>

                          <input
                            id={`durataFinale-${row.key}`}
                            type="number"
                            name="durataFinale"
                            value={row.durataFinale}
                            onChange={(e) => handleChange(i, e)}
                            placeholder="Durata in minuti"
                            min="1"
                            disabled={saving}
                          />

                          <textarea
                            id={`faseFinale-${row.key}`}
                            name="faseFinale"
                            value={row.faseFinale}
                            onChange={(e) => handleChange(i, e)}
                            placeholder="Descrivi la fase finale..."
                            rows={4}
                            disabled={saving}
                          />
                        </div>
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <div className="allenamento-actions">
          <button type="button" onClick={addRow} disabled={saving}>
            + Aggiungi Riga
          </button>

          <button type="submit" disabled={saving || loadingCategorie}>
            {saving ? 'Salvataggio...' : 'Salva Allenamenti'}
          </button>
        </div>

        <div className="allenamento-back">
          <button
            className="allenamento-custom-button"
            type="button"
            onClick={() => navigate('/gestionale')}
          >
            Torna al Gestionale
          </button>
        </div>
      </form>
    </div>
  );
}